import { useCallback, useEffect, useState } from 'react';

export type LoadState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly data: T };

/**
 * Runs `load` on mount and whenever `reload` is called. A stale response (from before the
 * latest reload or after unmount) is ignored, so the screen always shows the newest data.
 */
export function useLoad<T>(load: () => Promise<T>): [LoadState<T>, () => void] {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading' });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let current = true;
    load().then(
      (data) => {
        if (current) setState({ status: 'ready', data });
      },
      (error: unknown) => {
        console.error(error);
        if (current) setState({ status: 'failed' });
      },
    );
    return () => {
      current = false;
    };
    // `load` is defined inline by callers; `version` is the explicit reload trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const reload = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);
  return [state, reload];
}

/** Throws the Supabase error so `useLoad` shows the failure state instead of empty data. */
export function must<T>(result: { data: T | null; error: { message: string } | null }): T {
  if (result.error) throw new Error(result.error.message);
  if (result.data === null) throw new Error('no data');
  return result.data;
}
